"use client"

// Anonymous web-visitor beacon (ported from ProductBrain). Fires a pageview to the public
// /api/web/track on every client navigation, and a page_exit beacon when the tab is hidden/closed
// (for accurate dwell). Mounted once at the ROOT layout so it covers every surface on every host
// — public marketing pages AND the apps, anonymous or signed-in. When a Supabase session exists
// we attach the access token so the endpoint can stitch this visitor to the user ("converted").
import { Suspense, useEffect, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { supabase } from "@/lib/supabaseClient"
import { setProductUser } from "@/lib/analytics/trackProduct"

const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const
function readUtm(sp: URLSearchParams): Record<string, string> | null {
  const out: Record<string, string> = {}
  for (const k of UTM_KEYS) { const v = sp.get(`utm_${k}`); if (v) out[k] = v }
  return Object.keys(out).length ? out : null
}

// A lightweight, first-party DEVICE FINGERPRINT: a SHA-256 (truncated) over passive browser attributes
// (UA, languages, platform, screen, timezone, hardware). Stable per device/browser without storing
// anything, so the beacon can stitch cookieless or IP-changed hits from the same device into one
// visitor — more reliably than IP+UA. Computed once per page load and cached. Best-effort: any failure
// (no SubtleCrypto, etc.) returns null and the server falls back to IP+UA stitching.
let _fpPromise: Promise<string | null> | null = null
function getDeviceFp(): Promise<string | null> {
  if (_fpPromise) return _fpPromise
  _fpPromise = (async () => {
    try {
      if (typeof navigator === "undefined" || !globalThis.crypto?.subtle) return null
      const n = navigator as Navigator & { deviceMemory?: number }
      const s = window.screen
      const parts = [
        n.userAgent || "",
        (n.languages && n.languages.length ? n.languages.join(",") : n.language) || "",
        n.platform || "",
        String(n.hardwareConcurrency || ""),
        String(n.deviceMemory || ""),
        `${s.width}x${s.height}x${s.colorDepth}`,
        String(window.devicePixelRatio || ""),
        Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        String(n.maxTouchPoints || 0),
      ]
      const buf = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(parts.join("|")))
      return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 32)
    } catch { return null }
  })()
  return _fpPromise
}

export function WebTracker() {
  return <Suspense fallback={null}><WebTrackerInner /></Suspense>
}

function WebTrackerInner() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pathname || typeof window === "undefined") return
    const referrer = previousPathRef.current ?? (document.referrer || null)
    previousPathRef.current = pathname
    const utm = searchParams ? readUtm(new URLSearchParams(searchParams.toString())) : null
    ;(async () => {
      const headers: Record<string, string> = { "Content-Type": "application/json" }
      // When signed in on one of our apps we know the user: attach the Bearer token (server stitches the
      // visitor→user for "converted") AND send their id as external_user_id so the visitor is stamped with
      // a product-usage identity. That makes later product events (trackProduct / server trackServerEvent)
      // count toward active USERS, not just devices — the same identity a customer's app sends via identify.
      let externalUserId: string | null = null
      try {
        const { data } = await supabase.auth.getSession()
        const token = data.session?.access_token
        if (token) headers["Authorization"] = `Bearer ${token}`
        externalUserId = data.session?.user?.id ?? null
      } catch { /* anonymous — fine */ }
      setProductUser(externalUserId)
      const deviceFp = await getDeviceFp()
      void fetch("/api/web/track", {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ path: pathname, title: document.title || null, referrer, utm, event_type: "pageview", device_fp: deviceFp, external_user_id: externalUserId }),
        keepalive: true,
      }).catch(() => {})
    })()
  }, [pathname, searchParams])

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return
    if (typeof navigator.sendBeacon !== "function") return
    let exitSent = false
    const sendExit = () => {
      if (exitSent) return
      exitSent = true
      try {
        const body = JSON.stringify({ path: window.location.pathname, title: document.title || null, event_type: "page_exit" })
        navigator.sendBeacon("/api/web/track", new Blob([body], { type: "application/json" }))
      } catch { /* ignore */ }
    }
    const onVisibility = () => {
      if (document.visibilityState === "hidden") sendExit()
      else if (document.visibilityState === "visible") exitSent = false
    }
    window.addEventListener("pagehide", sendExit)
    document.addEventListener("visibilitychange", onVisibility)
    return () => { window.removeEventListener("pagehide", sendExit); document.removeEventListener("visibilitychange", onVisibility) }
  }, [])

  return null
}
